/**
 * ========================================
 * ChargeItemDefinition Extension Structure for Total Cost Price
 * ========================================
 * 
 * Extension URL: http://hl7.org/fhir/StructureDefinition/ChargeItemDefinition-totalCost
 * 
 * Structure:
 * extension: [
 *   {
 *     url: "http://hl7.org/fhir/StructureDefinition/ChargeItemDefinition-totalCost",
 *     extension: [
 *       {
 *         url: "type",
 *         valueCode: "base" | "surcharge" | "deduction" | "discount" | "tax" | "informational"
 *       },
 *       {
 *         url: "code",
 *         valueCodeableConcept: {
 *           coding: [
 *             {
 *               system: "http://terminology.hl7.org/CodeSystem/chargeitem-billingcodes",
 *               code: "1100",
 *               display: "Unvorhergesehene Auslagen"
 *             }
 *           ]
 *         }
 *       },
 *       {
 *         url: "amount",
 *         valueMoney: {
 *           value: 100.00,
 *           currency: "USD"
 *         }
 *       },
 *       {
 *         url: "factor",
 *         valueDecimal: 1.0
 *       }
 *     ]
 *   }
 * ]
 */

import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule, DatePipe, TitleCasePipe } from '@angular/common';
import { RouterLink } from '@angular/router';
import { MatTableModule } from '@angular/material/table';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ChargeItemDefinition, Bundle } from 'fhir/r4';
import { AdmittedPatientsComponent } from '../admitted-patients/admitted-patients.component';
import { DetailsBuilderObject } from '../detailz-viewz/details-builder.service';
import { EmptyStateComponent } from '../shared/empty-state/empty-state.component';
import { TableHeaderComponent } from '../table-header/table-header.component';
import { ChipsDirective } from '../chips.directive';
import { NaPipe } from '../shared/na.pipe';
import { DetailActionButton, DetailzViewzComponent } from '../detailz-viewz/detailz-viewz.component';
import { PatientAddressPipe } from '../shared/pipes/patient-address.pipe';
import { PatientAgePipe } from '../shared/pipes/patient-age.pipe';
import { PatientContactsPipe } from '../shared/pipes/patient-contacts.pipe';
import { PatientNamePipe } from '../shared/pipes/patient-name.pipe';
import { CodeableConcept2Pipe } from "../shared/codeable-concept2.pipe";
import { DetailBaseComponent } from '../shared/detail-base/detail-base.component';
import { StatusChangesService } from '../status-changes.service';
import { DynamicFormsV2Component } from '../shared/dynamic-forms-v2/dynamic-forms-v2.component';
import { formMetaData } from '../shared/dynamic-forms.interface2';
import { ReferenceDisplayDirective } from '../shared/reference-display.directive';
import { CustomTabsComponent } from "../shared/custom-tabs/custom-tabs.component";
import { UtilityService } from '../shared/utility.service';
import { map } from 'rxjs';
import { ChargesSearchService } from './charges-search.service';

@Component({
  selector: 'app-charges',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    MatTableModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatDividerModule,
    ReferenceDisplayDirective,
    MatProgressSpinnerModule,
    DatePipe,
    TitleCasePipe,
    EmptyStateComponent,
    TableHeaderComponent,
    ChipsDirective,
    NaPipe,
    CodeableConcept2Pipe,
    CustomTabsComponent
  ],
  providers: [PatientNamePipe, PatientContactsPipe, PatientAgePipe, PatientAddressPipe],

  templateUrl: './charges.component.html',
  styleUrls: ['./charges.component.scss']
})
export class ChargesComponent extends AdmittedPatientsComponent implements OnInit, OnDestroy {
  public utilityService = inject(UtilityService);
  public chargesSearchService = inject(ChargesSearchService);
  // Override table columns
  override displayedColumns: string[] = ['title', 'status', 'basePrice'];

  // Details builder for dialog
  chargeDetailsBuilder: DetailsBuilderObject = {
    resourceName: 'Charge Item Definition',
    resourceIcon: 'payments',
    specialHeader: {
      strongSectionKey: 'service_product',
      iconSectionKeys: ['status', 'total selling price'],
      contentSectionKeys: ['description', 'effectivePeriod']
    },
    groups: [

      {
        groupName: 'Pricing',
        groupIcon: 'attach_money',
        groupKeys: ['total selling price', 'total cost price',]
      },
      // {
      //   groupName: 'Applicability',
      //   groupIcon: 'rule',
      //   groupKeys: ['effectivePeriodDisplay', 'applicability']
      // }
    ]
  };

  override ngOnInit(): void {
    // STEP 0: Compute permissions for 'chargeItemDef' resource
    // This sets canAdd$ and canExport$ observables in the base component
    this.computePermissions('chargeItemDef');

    // Load org-wide charges
    this.chargesSearchService.init({ pageSize: 50 });
    this.tableDataSource = this.chargesSearchService.dataSource as any;
    
    // Subscribe to loading and counts
    // The search service automatically handles table filtering
    this.organizingSearchFilters(new Map<string, any[]>([
      ['status', ['active', 'draft', 'retired', 'unknown']]
    ]));
    
    // Initial fetch for active charges
    this.chargesSearchService.search({ status: 'active', tab: this.currentTab });


  }
  showReferenceDetails(reference: string): void {
    this.dialog.open(DetailBaseComponent, {
      maxWidth: '600px',
      maxHeight: '80vh',
      data: {
        url: reference,
      }
    })
  };
  private currentTab = 'all';
  private currentSearch = '';
  private currentStatus = 'active';

  onSearchChanged(searchText: string): void {
    this.currentSearch = searchText;
    this.triggerSearch();
  }

  onFilterChanged(event: { filterKey: string; values: string[] }): void {
    if (event.filterKey === 'status') {
      // If multiple selected, take the first one or default to active
      this.currentStatus = event.values.length > 0 ? event.values[0] : 'active';
      this.triggerSearch();
    }
  }

  private triggerSearch(): void {
    this.chargesSearchService.search({
      status: this.currentStatus,
      searchtext: this.currentSearch,
      tab: this.currentTab
    });
  }

  onAddCharge(): void {
    this.router.navigate(['/app/charges/add']);
  }

  onExportCharges(): void {
    const snapshot = this.tableDataSource.data;
    if (!snapshot.length) {
      console.warn('No charges to export.');
      return;
    }
    console.log('Export payload:', snapshot);
    // TODO: Implement actual export logic
  }

  showRow(charge: any): void {
    this.showChargeDetails(charge);
  }

  showChargeDetails(charge: any): void {
    const keysForBuilder = []
    const salesComponents = this.utilityService.seperateTotalPriceComponents(charge);
    const seperateTotalCostPriceComponents = this.separateTotalCostPriceComponents(charge);
    const toSendResourceData: any = {
      ...charge,
      ...(charge.batchLotNumber ? { 'batch lot number': charge.batchLotNumber } : {}),
      ...(charge.expirationDate ? { 'expiration date': charge.expirationDate } : {}),
      'total selling price': salesComponents ? new Intl.NumberFormat('en-NG', {
        style: 'currency',
        currency: salesComponents.currency || 'NGN',

      }).format(salesComponents.totalSellingPrice) : '',
      ...((seperateTotalCostPriceComponents?.totalCostPrice || 0) > 0 ? {
        'total cost price': seperateTotalCostPriceComponents ? new Intl.NumberFormat('en-NG', {
          style: 'currency',
          currency: charge.propertyGroup?.[0]?.priceComponent?.[0]?.amount?.currency || 'NGN',

        }).format(seperateTotalCostPriceComponents.totalCostPrice || 0) : ''
      } : {}),
    }
// $#$    // /* alert(JSON.stringify(seperateTotalPriceComponents) */);

    for (const key in salesComponents) {

      if (key === 'total selling price' || key === 'totalSellingPrice') continue;
      toSendResourceData[`selling price - ${key}`] = new Intl.NumberFormat('en-NG', {
        style: 'currency',
        currency: salesComponents.currency || 'NGN',

      }).format(salesComponents[key]);
      keysForBuilder.push(`selling price - ${key}`);
    }
    for (const key in seperateTotalCostPriceComponents) {
      if (key === 'total cost price' || key === 'totalCostPrice') continue;
      toSendResourceData[`cost price - ${key.replace('total', 'total ')}`] = new Intl.NumberFormat('en-NG', {
        style: 'currency',
        currency: charge.propertyGroup?.[0]?.priceComponent?.[0]?.amount?.currency || 'NGN',

      }).format((seperateTotalCostPriceComponents as any)[key]);
      keysForBuilder.push(`cost price - ${key.replace('total', 'total ')}`);
    }

    if (keysForBuilder.length > 0) {
      // AND IF THERE IS NO SUCH GROUP BEFORE
      if (!this.chargeDetailsBuilder.groups.find(g => g.groupName === 'Price Breakdown')) {
        this.chargeDetailsBuilder.groups.push({
          groupName: 'Price Breakdown',
          groupIcon: 'breakdown',
          groupKeys: keysForBuilder
        });
      }
    }
    const productService = {
      ...(charge.instance ? {
        product: charge.instance[0]
      } : {}),
      ...(charge.code ? {
        service: charge.code.text || charge.code?.coding?.[0]?.display
      } : {})
    }
    toSendResourceData[
      'service_product'] = productService.product ?? productService.service

    productService.product ? toSendResourceData['product'] = productService.product : null;
    productService.service ? toSendResourceData['service'] = productService.service : null;
    //unshift ny if absent
    const existingGroup = this.chargeDetailsBuilder.groups.find(g => g.groupName === 'Product / Service');
    const prodKeys = [...Object.keys(productService), 'status'];
    if (charge.batchLotNumber && !prodKeys.includes('batch lot number')) prodKeys.push('batch lot number');
    if (charge.expirationDate && !prodKeys.includes('expiration date')) prodKeys.push('expiration date');

    if (!existingGroup) {
      this.chargeDetailsBuilder.groups.unshift({
        groupName: 'Product / Service',
        groupIcon: 'category',
        groupKeys: prodKeys
      });
    } else {
      existingGroup.groupKeys = prodKeys;
    }

    const dref = this.dialog.open(DetailzViewzComponent, {
      maxHeight: '93vh',
      maxWidth: '650px',
      width: '100%',
      data: {
        resourceData: toSendResourceData,
        detailsBuilderObject: this.chargeDetailsBuilder,
        actionButtons: <DetailActionButton[]>[
          // {            key: 'edit',
          //   label: 'Edit Charge',
          //   icon: 'edit',

          // },
          {
            key: 'changeStatus',
            label: 'Change Status',
            icon: 'swap_horiz',
            capabilities: [{ resource: 'chargeItemDef', action: 'update' }]
          },

        ]
      }
    });

    dref.componentInstance.actionInvoked.subscribe(({ key }) => {
// $#$      // /* alert(JSON.stringify(actionKey) */);
      if (key === 'changeStatus') {
        // this.editCharge(charge);
        dref.close();
        const dynF = this.dialog.open(
          DynamicFormsV2Component, {
          data: {
            formMetaData: <formMetaData>{
              formName: 'Edit Charge Status',
              closeDialogOnSubmit: true,
              formDescription: 'Modify the status of the charge item definition.',
              submitText: 'Update Status',
              showSubmitButton: true,

            },
            formFields: [
              //status field
              {
                generalProperties: {
                  fieldApiName: 'status',
                  fieldLabel: 'Status',
                  fieldType: 'CodeField',
                },
                data: "draft | active | retired | unknown".split(' | '),
              }
            ]
          }
        }
        )
        dynF.afterClosed().subscribe((formResult) => {
          if (formResult && formResult.values) {
            const newStatus = formResult.values['status'];
            this.statusChangesService.changeStatus('ChargeItemDefinition', 'org', charge.id || '', newStatus);
          }
        });
      } else if (key === 'edit') {
        // this.statusChangesService.changeStatus('ChargeItemDefinition', 'org', charge.id || '', newStatus);
      }
    });

  }
  statusChangesService = inject(StatusChangesService);
  editCharge(charge: ChargeItemDefinition): void {
    this.router.navigate(['/app/charges/add'], {
      queryParams: { id: charge.id }
    });
  }

  //separateTotalCostPriceComponents

  private separateTotalCostPriceComponents(chargeItemDef: ChargeItemDefinition): {
    totalBase?: number;
    totalSurcharge?: number;
    totalDeduction?: number;
    totalDiscount?: number;
    totalTax?: number;
    totalInformational?: number;
    totalCostPrice?: number;
    // currency?: string;
  } {
    const result: any = {}
    // {
    //   totalBase: 0,
    //   totalSurcharge: 0,
    //   totalDeduction: 0,
    //   totalDiscount: 0,
    //   totalTax: 0,
    //   totalInformational: 0,
    //   // currency: 'USD' // Default currency
    // };

    if (!chargeItemDef.extension || chargeItemDef.extension.length === 0) {
      console.log('No extensions found on ChargeItemDefinition');
      return result;
    }

    // Find all totalCost extensions (there might be multiple)
    const totalCostExtensions = chargeItemDef.extension.filter(ext =>
      ext.url === 'http://hl7.org/fhir/StructureDefinition/ChargeItemDefinition-totalCost'
    );

    if (totalCostExtensions.length === 0) {
      console.log('No totalCost extensions found');
      return result;
    }

    // Process each totalCost extension
    totalCostExtensions.forEach(totalCostExt => {
      if (!totalCostExt.extension) return;

      const subExtensions = totalCostExt.extension;

      // Find type and amount
      const typeExt = subExtensions.find(e => e.url === 'type');
      const amountExt = subExtensions.find(e => e.url === 'amount');

      if (!typeExt || !amountExt) return;

      const type = (typeExt as any).valueCode as string;
      const money = (amountExt as any).valueMoney;

      if (!money || typeof money.value !== 'number') return;

      const amount = money.value;
      const currency = money.currency || 'USD';

      // Store currency (use first one encountered)
      if (result.currency === 'USD' && currency !== 'USD') {
        result.currency = currency;
      }
// $#$      // /* alert(amount); */
      // Aggregate amounts by type
      switch (type?.toLowerCase()) {

        case 'base':
          result.totalBase = (result.totalBase || 0) + amount;
// $#$          // /* alert(JSON.stringify(result) */);
          break;
        case 'surcharge':
          result.totalSurcharge = (result.totalSurcharge || 0) + amount;
          break;
        case 'deduction':
          result.totalDeduction = (result.totalDeduction || 0) + amount;
          break;
        case 'discount':
          result.totalDiscount = (result.totalDiscount || 0) + amount;
          break;
        case 'tax':
          result.totalTax = (result.totalTax || 0) + amount;
          break;
        case 'informational':
          result.totalInformational = (result.totalInformational || 0) + amount;
          break;
        default:
          console.warn(`Unknown price component type: ${type}`);
      }
    });
// $#$    // /* alert(JSON.stringify(result) */);
    console.log('✅ Aggregated total cost price components from extension:', result);
    // Calculate total cost price
    result.totalCostPrice =
      (result.totalBase || 0) +
      (result.totalSurcharge || 0) +
      (result.totalTax || 0) -
      (result.totalDeduction || 0) -
      (result.totalDiscount || 0);

    return result;
  }

  deleteCharge(charge: ChargeItemDefinition): void {
    if (!charge.id) return;

    if (confirm(`Are you sure you want to delete charge: ${charge.title}?`)) {
      this.http.delete(`${this.backendUrl}/ChargeItemDefinition/${charge.id}`)
        .subscribe({
          next: () => {
            console.log('✅ Charge deleted successfully');
            this.chargesSearchService.refresh(); // Reload list
          },
          error: (err) => {
            console.error('❌ Error deleting charge:', err);
            this.errorService.openandCloseError('Failed to delete charge');
          }
        });
    }
  }

  /**
   * Extract base price from ChargeItemDefinition
   * Used as callback in transformData to create basePriceDisplay additional key
   */
  private getBasePrice(charge: ChargeItemDefinition): string {
    const priceComponent = charge.propertyGroup?.[0]?.priceComponent?.[0];
    if (priceComponent?.amount) {
      const currency = priceComponent.amount.currency || '';
      const value = priceComponent.amount.value || 0;
      return `${currency} ${value.toFixed(2)}`;
    }
    return 'N/A';
  }

  /**
   * Format code for display
   * Used as callback in transformData to create codeDisplay additional key
   */
  private getCodeDisplay(charge: ChargeItemDefinition): string {
    if (charge.code) {
      return charge.code.text ||
        charge.code.coding?.[0]?.display ||
        charge.code.coding?.[0]?.code ||
        'N/A';
    }
    return 'N/A';
  }

  /**
   * Format effective period for display
   * Used as callback in transformData to create effectivePeriodDisplay additional key
   */
  private getEffectivePeriod(charge: ChargeItemDefinition): string {
    if (charge.effectivePeriod) {
      const start = charge.effectivePeriod.start ? new Date(charge.effectivePeriod.start).toLocaleDateString() : '';
      const end = charge.effectivePeriod.end ? new Date(charge.effectivePeriod.end).toLocaleDateString() : 'Ongoing';
      return start ? `${start} - ${end}` : 'N/A';
    }
    return 'N/A';
  }

  /**
   * Extract total cost from ChargeItemDefinition extension
   * Extension URL: http://hl7.org/fhir/StructureDefinition/ChargeItemDefinition-totalCost
   */
  private getTotalCostFromExtension(charge: ChargeItemDefinition): string {
    if (!charge.extension || charge.extension.length === 0) {
      return 'N/A';
    }

    const totalCostExt = charge.extension.find(ext =>
      ext.url === 'http://hl7.org/fhir/StructureDefinition/ChargeItemDefinition-totalCost'
    );

    if (!totalCostExt || !totalCostExt.extension) {
      return 'N/A';
    }

    // Extract amount from sub-extension
    const amountExt = totalCostExt.extension.find(subExt => subExt.url === 'amount');
    if (amountExt && (amountExt as any).valueMoney) {
      const money = (amountExt as any).valueMoney;
      const currency = money.currency || '';
      const value = money.value || 0;
      return `${currency} ${value.toFixed(2)}`;
    }

    return 'N/A';
  }

  override ngOnDestroy(): void {
    // Clean up org-wide resources for charges
    console.log('Cleaning up charges from orgWideResources');

    // if (this.stateService.orgWideResources.chargeItemDefinitions) {
    //   this.stateService.orgWideResources.chargeItemDefinitions.next([]);
    // }

    // Call parent cleanup
    super.ngOnDestroy?.();
  }

  processTabChanges(ev: string) {
// $#$    // /* alert(ev); */
    this.currentTab = ev || 'all';
    this.triggerSearch();
  }
}
